import { Injectable, OnDestroy } from '@angular/core';
import { Observable, BehaviorSubject, of, Subscription } from 'rxjs';
import { map, catchError, switchMap, finalize } from 'rxjs/operators';
import { Router } from '@angular/router';

import { UserModel } from '../_models/user.model';
import { AuthModel } from '../_models/auth.model';
import { AuthHTTPService } from './auth-http';
import { environment } from 'src/environments/environment';
import { LoadingBarService } from '../../shared/services/loading-bar.service';
import * as CryptoJs from 'crypto-js';

const LOGIN_URL = `${environment.apiUrl}/Authenticate`;

@Injectable({
  providedIn: 'root',
})
export class AuthService implements OnDestroy {
  static getLoginUrlNew(): any { 
    let redirectUrl = `${environment.appUrl}/auth/external`;
    let loginURL =  `${environment.b2cUrl}/authorize?p=B2C_1A_signup_signin&client_id=${environment.clientId}&nonce=defaultNonce&redirect_uri=${encodeURIComponent(redirectUrl)}&scope=openid&response_type=id_token&prompt=login`;
    
    const {
      location: { search },
    } = window;
    if (search) {
      const params = new URLSearchParams(search);
      const isSkipMFA = params.get('isSkipMFA');

      if(isSkipMFA == "true"){
        loginURL = loginURL + "&isSkipMFA=true"
      }
    }

    return loginURL;
  }
  // private fields
  private unsubscribe: Subscription[] = []; // Read more: => https://brianflove.com/2016/12/11/anguar-2-unsubscribe-observables/
  private authLocalStorageToken = `${environment.appVersion}-${environment.USERDATA_KEY}`;
  private secretKey = 'cryptoKey';

  // public fields
  currentUser$: Observable<UserModel>;
  isLoading$: Observable<boolean>;
  currentUserSubject: BehaviorSubject<UserModel>;
  isLoadingSubject: BehaviorSubject<boolean>;
  emailSentOn: string;
  get currentUserValue(): UserModel {
    return this.currentUserSubject.value;
  }

  set currentUserValue(user: UserModel) {
    this.currentUserSubject.next(user);
  }

  constructor(
    private authHttpService: AuthHTTPService,
    private router: Router,
    private loadingService: LoadingBarService,
  ) {
    this.isLoadingSubject = new BehaviorSubject<boolean>(false);
    this.currentUserSubject = new BehaviorSubject<UserModel>(undefined);
    this.currentUser$ = this.currentUserSubject.asObservable();
    this.isLoading$ = this.isLoadingSubject.asObservable();

    // I don't know if I need the following, it is duplicating my calls (Metronic's original):
    /* const subscr = this.getUserByToken().subscribe();
    this.unsubscribe.push(subscr); */
  }

  // public methods
  getLoginUrl() {
    let redirectUrl = `${environment.appUrl}/auth/external`;
    return `${environment.b2cUrl}/authorize?p=B2C_1A_signup_signin&client_id=${environment.clientId}&nonce=defaultNonce&redirect_uri=${encodeURIComponent(redirectUrl)}&scope=openid&response_type=id_token&prompt=login`
  }

  login(username: string, password: string): Observable<UserModel> {
    this.isLoadingSubject.next(true);
    return this.authHttpService.login(username, password).pipe(
      map((auth: AuthModel) => {
        const result = this.setAuthFromLocalStorage(auth);
        return result;
      }),
      switchMap(() => this.getUserByToken()),
      catchError((err) => {
        console.error('err', err);
        return of(undefined);
      }),
      finalize(() => this.isLoadingSubject.next(false))
    );
  }

  logout() {
    localStorage.clear();
    sessionStorage.removeItem('postAuthConsentAccepted');
    this.currentUserSubject.next(undefined);
    this.loadingService.stopAllLoadings();
    this.router.navigate(['/auth/login']);
  }

  getUserByToken(): Observable<UserModel> {
    const user = this.getAuthFromLocalStorage();
    if (!user || !user.token) {
      return of(undefined);
    }

    // We don't have an endpoint to get the user based on the token
    // instead the Authenticate endpoint already return the user
    this.currentUserSubject.next(user);
    return of(user);

    /* this.isLoadingSubject.next(true);
    return this.authHttpService.getUserByToken(auth.token).pipe(
      map((user: UserModel) => {
        if (user) {
          this.currentUserSubject = new BehaviorSubject<UserModel>(user);
        } else {
          this.logout();
        }
        return user;
      }),
      finalize(() => this.isLoadingSubject.next(false))
    ); */
  }

  // need create new user then login
  registration(user: UserModel): Observable<any> {
    this.isLoadingSubject.next(true);
    return this.authHttpService.createUser(user).pipe(
      map(() => {
        this.isLoadingSubject.next(false);
      }),
      switchMap(() => this.login(user.username, user.password)),
      catchError((err) => {
        console.error('err', err);
        return of(undefined);
      }),
      finalize(() => this.isLoadingSubject.next(false))
    );
  }

  forgotPassword(email: string): Observable<boolean> {
    this.isLoadingSubject.next(true);
    return this.authHttpService
      .forgotPassword(email)
      .pipe(finalize(() => this.isLoadingSubject.next(false)));
  }

  forgotUsername(email: string): Observable<string> {
    this.isLoadingSubject.next(true);
    return this.authHttpService
      .forgotUsername(email)
      .pipe(finalize(() => this.isLoadingSubject.next(false)));
  }

  createPassword(user: UserModel, password: string): Observable<boolean> {
    this.isLoadingSubject.next(true);
    return this.authHttpService
      .createPassword(password)
      .pipe(finalize(() => this.isLoadingSubject.next(false)));
  }

  public userHasPassword(): boolean {
    return this.currentUserValue.type !== 'PCUSTNAP';
  }

  public userIsMember(): boolean {
    return this.currentUserValue.type === 'CUSTWP' ||
      this.currentUserValue.type === 'CUSTWCP';
  }

  public userIsProspect(): boolean {
    return this.currentUserValue.type === 'PCUSTWAP' ||
      this.currentUserValue.type === 'PCUSTNAP';
  }

  encryptAuth(token: string): string {
    return CryptoJs.AES.encrypt(token, this.secretKey).toString();
  }

  setAuthFromLocalStorage(auth: AuthModel): boolean {
    if (auth && auth.token) {
        const encryptedAuth = this.encryptAuth(auth.token);
        localStorage.setItem(this.authLocalStorageToken, JSON.stringify({ ...auth, token: encryptedAuth }));
        return true;
    }
        return false;
  }
  
  decryptAuth(token: string): string {
    const bytes = CryptoJs.AES.decrypt(token, this.secretKey);
    return bytes.toString(CryptoJs.enc.Utf8);
  }

  // private methods
  private getAuthFromLocalStorage(): UserModel {
    try {
      const encryptedAuth = localStorage.getItem(this.authLocalStorageToken);
      const encryptedAuthObj = JSON.parse(encryptedAuth);
      const decryptedToken = encryptedAuthObj && this.decryptAuth(encryptedAuthObj.token);
      const userData = 
        {...encryptedAuthObj, token: decryptedToken}
      ;
      return userData;
    } catch (error) {
      console.error(error);
      return undefined;
    }
  }

  ngOnDestroy() {
    this.unsubscribe.forEach((sb) => sb.unsubscribe());
  }
}
